import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import axios from 'axios';
import { MapPin, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RequestCraneQuote = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loadIdParam = searchParams.get('loadId') || '';

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    email: '',
    phone: '',
    load_id: loadIdParam,
    date_required: '',
    time_required: '',
    address: '',
    site_contact_name: '',
    site_contact_phone: '',
    site_uhf_channel: '',
    site_inductions: '',
    load_description: '',
    load_weight: '',
    load_dimensions: '',
    lifting_points: '',
    pin_location_lat: null,
    pin_location_lng: null,
    message: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Photo must be under 10MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPhoto(reader.result);
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handlePinDrop = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData(prev => ({
          ...prev,
          pin_location_lat: position.coords.latitude,
          pin_location_lng: position.coords.longitude
        }));
        toast.success('Location pinned successfully');
      },
      () => toast.error('Unable to get your location. Please ensure location access is enabled.')
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ['name', 'email', 'phone', 'date_required', 'time_required', 'address', 'site_contact_name', 'site_contact_phone', 'site_inductions', 'load_description', 'load_weight', 'load_dimensions', 'lifting_points'];
    const missing = required.filter(f => !formData[f]);
    if (missing.length) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/crane-quote-requests`, {
        ...formData,
        photo: photo
      });
      setSubmitted(true);
      toast.success('Crane quote request submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
          <Card className="max-w-md w-full mx-4 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">✓</span>
            </div>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2">Request Submitted</h2>
            <p className="text-gray-600 mb-6">Your crane quote request has been received. Our team will review the details and provide a quote shortly.</p>
            <Button onClick={() => navigate('/')} className="bg-[#1e3a5f] hover:bg-[#2d5a8f] text-white">Back to Home</Button>
          </Card>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          <h1 className="text-3xl font-bold text-[#1e3a5f] text-center mb-2">Request a Crane Quote</h1>
          <p className="text-gray-600 text-center mb-8">Complete the form below and we'll connect you with available crane operators.</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Contact Details */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Your Details</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input name="name" value={formData.name} onChange={handleChange} required placeholder="Full name" data-testid="crane-name" />
                </div>
                <div>
                  <Label>Company Name</Label>
                  <Input name="company_name" value={formData.company_name} onChange={handleChange} placeholder="Company (optional)" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="your@email.com" data-testid="crane-email" />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input name="phone" value={formData.phone} onChange={handleChange} required placeholder="04XX XXX XXX" data-testid="crane-phone" />
                </div>
              </div>
            </Card>

            {/* Job Details */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Job Details</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Date Required *</Label>
                  <Input name="date_required" type="date" value={formData.date_required} onChange={handleChange} required data-testid="crane-date" />
                </div>
                <div>
                  <Label>Time *</Label>
                  <Input name="time_required" type="time" value={formData.time_required} onChange={handleChange} required data-testid="crane-time" />
                </div>
                <div className="md:col-span-2">
                  <Label>Address *</Label>
                  <Input name="address" value={formData.address} onChange={handleChange} required placeholder="Full site address" data-testid="crane-address" />
                </div>
                <div>
                  <Label>Load ID (if linked to a posted load)</Label>
                  <Input name="load_id" value={formData.load_id} onChange={handleChange} placeholder="e.g. a363c28c" />
                </div>
              </div>
            </Card>

            {/* Site Contact */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Site Contact & Requirements</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Site Contact Name *</Label>
                  <Input name="site_contact_name" value={formData.site_contact_name} onChange={handleChange} required placeholder="On-site contact person" data-testid="crane-site-contact" />
                </div>
                <div>
                  <Label>Site Contact Phone *</Label>
                  <Input name="site_contact_phone" value={formData.site_contact_phone} onChange={handleChange} required placeholder="04XX XXX XXX" data-testid="crane-site-phone" />
                </div>
                <div>
                  <Label>Site UHF Channel</Label>
                  <Input name="site_uhf_channel" value={formData.site_uhf_channel} onChange={handleChange} placeholder="e.g. Channel 40" />
                </div>
                <div>
                  <Label>Site Inductions Required? *</Label>
                  <select name="site_inductions" value={formData.site_inductions} onChange={handleChange} required className="w-full h-10 border rounded-md px-3 text-sm" data-testid="crane-inductions">
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
              </div>
            </Card>

            {/* Load Information */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Load Information</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Load Description *</Label>
                  <Textarea name="load_description" value={formData.load_description} onChange={handleChange} required placeholder="Describe the load to be lifted..." rows={3} data-testid="crane-load-desc" />
                </div>
                <div>
                  <Label>Load Weight *</Label>
                  <Input name="load_weight" value={formData.load_weight} onChange={handleChange} required placeholder="e.g. 15 tonnes" data-testid="crane-weight" />
                </div>
                <div>
                  <Label>Load Dimensions *</Label>
                  <Input name="load_dimensions" value={formData.load_dimensions} onChange={handleChange} required placeholder="e.g. 6m x 3m x 2.5m" data-testid="crane-dimensions" />
                </div>
                <div>
                  <Label>Lifting Points Available? *</Label>
                  <select name="lifting_points" value={formData.lifting_points} onChange={handleChange} required className="w-full h-10 border rounded-md px-3 text-sm" data-testid="crane-lifting-points">
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
              </div>
            </Card>

            {/* Pin Drop & Photo */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Location & Photo</h2>
              
              {/* Pin Drop */}
              <div className="mb-4">
                <Label className="mb-2 block">Pin Drop Location</Label>
                <Button type="button" variant="outline" onClick={handlePinDrop} className="text-sm">
                  <MapPin className="w-4 h-4 mr-1" /> Use My Current Location
                </Button>
                {formData.pin_location_lat && (
                  <p className="text-xs text-green-700 mt-2">
                    Location pinned: {formData.pin_location_lat.toFixed(6)}, {formData.pin_location_lng.toFixed(6)}
                    <button type="button" onClick={() => setFormData(prev => ({ ...prev, pin_location_lat: null, pin_location_lng: null }))} className="ml-2 text-red-500 hover:underline">Remove</button>
                  </p>
                )}
              </div>

              {/* Photo Upload */}
              <div>
                <Label className="mb-2 block">Photo</Label>
                {photoPreview ? (
                  <div className="relative inline-block">
                    <img src={photoPreview} alt="Upload" className="w-40 h-40 object-cover rounded border" />
                    <button type="button" onClick={() => { setPhoto(null); setPhotoPreview(null); }} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <label className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 cursor-pointer border border-blue-300 rounded px-3 py-1.5">
                    Upload Photo
                    <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                  </label>
                )}
                <p className="text-xs text-gray-400 mt-1">Optional. Max 10MB. JPG, PNG supported.</p>
              </div>
            </Card>

            {/* Message */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Additional Information</h2>
              <div>
                <Label>Message for Admin</Label>
                <Textarea name="message" value={formData.message} onChange={handleChange} placeholder="Any additional information or special requirements..." rows={4} />
              </div>
            </Card>

            <Button type="submit" disabled={loading} className="w-full bg-red-600 hover:bg-red-700 text-white text-lg py-6" data-testid="submit-crane-quote">
              {loading ? 'Submitting...' : 'Submit Crane Quote Request'}
            </Button>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default RequestCraneQuote;
